import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, ChevronUp } from "lucide-react";

const LOGO_URL = "/assets/images/logo.png";

// Social Media Icons
const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socialLinks = [
    { name: "LinkedIn", href: "https://www.linkedin.com/company/global-school-leaders-consortium/", icon: LinkedInIcon },
    { name: "Facebook", href: "https://www.facebook.com/globalslc", icon: FacebookIcon },
    { name: "Instagram", href: "https://www.instagram.com/global_slc", icon: InstagramIcon },
  ];

  return (
    <footer className="bg-gradient-to-br from-slate-100 via-slate-50 to-white border-t border-slate-200">
      <div className="container-custom py-16">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Logo & Tagline */}
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="GSLC Logo" className="h-20 mb-6" />
            <p className="text-slate-600 leading-relaxed max-w-md">
              Connecting Leaders. Inspiring Change. Transforming Education.
            </p>
            <p className="font-heading text-xl text-red-600 mt-4 italic">
              "Connect | Collaborate | Contribute"
            </p>
            
            {/* Social Media Links */}
            <div className="flex gap-4 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${social.name.toLowerCase()}`}
                  className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 hover:bg-red-600 hover:text-white transition-all duration-300"
                  aria-label={social.name}
                >
                  <social.icon />
                </a>
              ))}
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg text-slate-800 mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { name: 'About Us', href: '/about' },
                { name: 'Learning Forums', href: '/learning-forums' },
                { name: 'Programs', href: '/programs' },
                { name: 'Advisory Board', href: '/advisory-board' },
                { name: 'Our Partners', href: '/partners' },
                { name: 'Careers', href: '/careers' },
                { name: 'Contact', href: '/contact' }
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-slate-500 hover:text-red-600 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg text-slate-800 mb-4">Contact</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-slate-500">
                <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm">2nd Floor, 214, Sankalp Tower, Jaipur, Rajasthan</p>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-red-600" />
                <a href="mailto:info@gslc.co.in" className="text-slate-500 hover:text-red-600 transition-colors text-sm">
                  info@gslc.co.in
                </a>
              </div>
            </div>
            
            {/* Google Maps Link */}
            <a
              href="https://maps.app.goo.gl/bkGk3pDUUMbNYbh5A"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4 text-red-600 hover:text-blue-700 text-sm transition-colors"
            >
              View on Google Maps →
            </a>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-slate-200">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Global School Leaders' Consortium. All rights reserved.
          </p>
          <div className="flex items-center gap-6 mt-4 md:mt-0">
            {/* Social Links in bottom bar too */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={`bottom-${social.name}`}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-red-600 transition-colors"
                  aria-label={social.name}
                >
                  <social.icon />
                </a>
              ))}
            </div>
            <button
              onClick={scrollToTop}
              data-testid="scroll-to-top-btn"
              className="text-slate-400 hover:text-red-600 transition-colors flex items-center gap-2"
            >
              Back to Top <ChevronUp size={16} />
            </button>
          </div>

          {/* Designer Credit */}
          <div className="mt-6 pt-6 border-t border-slate-200 text-center">
            <p className="text-slate-400 text-sm">
              Designed by{' '}
              <a 
                href="https://www.zenithmarketingindia.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-red-600 hover:text-blue-700 transition-colors font-medium"
                title="Zenith Marketing India - Digital Marketing & Web Design"
              >
                Zenith Marketing India
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
